import { api } from './api';
import { collectPages } from './all-pages';
import type { DiagramKit, DiagramResponse } from '@xdface-vault/shared';

/**
 * The thumbnail API (backend work in parallel) adds `thumbnailUrl` to diagram
 * list/get responses. Declared locally so the web slice compiles against the
 * contract before `@xdface-vault/shared` ships the field.
 */
export type DiagramSummary = DiagramResponse & { thumbnailUrl?: string };

export interface DiagramWithUrls extends DiagramResponse {
  mxfileUrl: string;
  uploadUrl: string;
  irUrl: string;
  /** Presigned GET URL of the auto-generated PNG thumbnail. */
  thumbnailUrl?: string;
}

export interface ListDiagramsResponse {
  data: DiagramSummary[];
  pagination: { cursor?: string; hasMore: boolean };
}

export interface CreateDiagramInput {
  title: string;
  kit: DiagramKit;
  tags?: string[];
  mxfile?: string;
}

export interface UpdateDiagramMetaInput {
  title?: string;
  tags?: string[];
  expectedVersion: number;
}

export interface CompilePageInput {
  page: {
    id: string;
    name: string;
    kind?: DiagramKit;
    nodes: Array<{
      id: string;
      kind: string;
      label: string;
      x: number;
      y: number;
      w: number;
      h: number;
      style?: string;
    }>;
    edges: Array<{
      id?: string;
      from: string;
      to: string;
      label?: string;
      style?: string;
    }>;
  };
  expectedVersion: number;
}

export const diagramApi = {
  async listDiagrams(): Promise<DiagramSummary[]> {
    return collectPages((cursor) =>
      api.get<ListDiagramsResponse>('/diagrams', { limit: '100', ...(cursor && { cursor }) }),
    );
  },

  async getDiagram(id: string): Promise<DiagramWithUrls> {
    return api.get<DiagramWithUrls>(`/diagrams/${id}`);
  },

  async createDiagram(input: CreateDiagramInput): Promise<DiagramWithUrls> {
    return api.post<DiagramWithUrls>('/diagrams', input);
  },

  async updateDiagramMeta(id: string, input: UpdateDiagramMetaInput): Promise<DiagramResponse> {
    return api.put<DiagramResponse>(`/diagrams/${id}`, input);
  },

  async saveMxfile(id: string, mxfile: string, expectedVersion: number): Promise<DiagramResponse> {
    return api.put<DiagramResponse>(`/diagrams/${id}/body`, { mxfile, expectedVersion });
  },

  async compilePage(id: string, input: CompilePageInput): Promise<DiagramResponse> {
    return api.post<DiagramResponse>(`/diagrams/${id}/compile`, input);
  },

  async deleteDiagram(id: string): Promise<void> {
    await api.delete(`/diagrams/${id}`);
  },

  /** Presigned PUT URL (15 min) for the auto-generated PNG thumbnail. */
  async getThumbnailUploadUrl(
    id: string,
    expectedVersion: number,
  ): Promise<{ uploadUrl: string; thumbnailKey: string; version: number }> {
    return api.post<{ uploadUrl: string; thumbnailKey: string; version: number }>(
      `/diagrams/${id}/thumbnail/presigned`,
      { expectedVersion },
    );
  },

  /** Browser-side PUT direct to S3, same pattern as the course image upload. */
  async uploadThumbnail(uploadUrl: string, blob: Blob): Promise<void> {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/png' },
      body: blob,
    });
    if (!res.ok) {
      throw new Error('Error al subir la miniatura');
    }
  },
};
